import { Link } from "react-router-dom";
import {
    ArrowRight,
    ShieldCheck,
    FlaskConical,
    Anchor,
    Train,
    FileCheck2,
    Network,
} from "lucide-react";

const QA_STEPS = [
    {
        n: "01",
        title: "Origin Verification",
        body: "Mine certificates, JORC reserves, and historical analysis records pulled before contract.",
    },
    {
        n: "02",
        title: "Load-Port Sampling",
        body: "Independent sampling by SGS / CCIC / Intertek at the load port. ASTM / ISO compliant.",
    },
    {
        n: "03",
        title: "Vessel Survey & Draft",
        body: "Independent draft survey & vessel hold inspection prior to loading.",
    },
    {
        n: "04",
        title: "Discharge Inspection",
        body: "Re-sampling and analysis at discharge port — buyer present, results binding.",
    },
    {
        n: "05",
        title: "Certificate of Analysis",
        body: "Full COA shared before BL release. Specs are contractually binding, not advisory.",
    },
];

const LOGISTICS = [
    {
        icon: Anchor,
        title: "Port Network",
        body: "Operations at Krishnapatnam, Kakinada, Mundra, Kandla, Tuticorin, Paradip, and Visakhapatnam.",
    },
    {
        icon: Train,
        title: "Inland Movement",
        body: "Rake placement and road dispatch handled in-house — straight to your stockyard.",
    },
    {
        icon: Network,
        title: "Vessel Chartering",
        body: "Panamax, Supramax, Handysize — dedicated chartering desk negotiates freight at parity.",
    },
    {
        icon: FileCheck2,
        title: "Trade Finance",
        body: "LC at sight, DA, DP and structured trade finance via tier-1 banks — paperwork handled end-to-end.",
    },
];

export default function Quality() {
    return (
        <div data-testid="page-quality">
            <section className="relative pt-40 pb-20 bg-coal-900 border-b border-white/10 overflow-hidden">
                <div className="absolute right-0 top-0 w-1/2 h-full opacity-40">
                    <img
                        src="https://images.unsplash.com/photo-1767853542172-7bfd8a3a91dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxidWxrJTIwY2FyZ28lMjBzaGlwJTIwb2NlYW4lMjBwb3J0fGVufDB8fHx8MTc3ODY4Nzk3M3ww&ixlib=rb-4.1.0&q=85"
                        alt="Cargo vessel"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-coal-900 via-coal-900/85 to-transparent" />
                </div>
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-6">
                        Quality & Logistics
                    </div>
                    <h1 className="font-display font-bold text-5xl sm:text-7xl uppercase tracking-tighter leading-[0.92] text-white max-w-4xl">
                        Spec on paper.
                        <br />
                        Spec in the rake.
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg text-zinc-300 leading-relaxed">
                        The contracted spec is the delivered spec. Our QA
                        backbone is built to guarantee it — from origin mine
                        face to your weighbridge.
                    </p>
                </div>
            </section>

            {/* QA STEPS */}
            <section className="section bg-coal-900">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="grid lg:grid-cols-12 gap-10 mb-14">
                        <div className="lg:col-span-5">
                            <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-4">
                                5-stage QA Protocol
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white leading-[1]">
                                Inspection-led
                                <br />
                                from origin to gate.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 text-zinc-300 leading-relaxed text-lg">
                            <p>
                                Every cargo we ship is governed by a strict
                                5-stage QA protocol with independent third-party
                                inspection at every checkpoint. No exceptions,
                                no in-house testing, no opaque numbers.
                            </p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
                        {QA_STEPS.map((s) => (
                            <div key={s.n} className="bg-coal-800 p-6 lg:p-8">
                                <div className="font-display text-5xl font-bold text-ember mb-4 tabular tracking-tighter">
                                    {s.n}
                                </div>
                                <h4 className="font-display text-lg font-semibold text-white mb-3 leading-tight">
                                    {s.title}
                                </h4>
                                <p className="text-zinc-400 text-sm leading-relaxed">
                                    {s.body}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 flex flex-wrap items-center gap-6 text-zinc-500 text-xs tracking-[0.2em] uppercase">
                        <span className="flex items-center gap-2 text-ember">
                            <ShieldCheck className="w-4 h-4" /> Independent
                            Inspectors
                        </span>
                        <span>SGS</span>
                        <span>CCIC</span>
                        <span>Intertek</span>
                        <span>Geo-Chem</span>
                        <span>ALS</span>
                    </div>
                </div>
            </section>

            {/* LOGISTICS */}
            <section className="section bg-coal-800 border-y border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-4">
                        Logistics Backbone
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white max-w-3xl">
                        End-to-end ownership of
                        <br />
                        every tonne we trade.
                    </h2>
                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
                        {LOGISTICS.map((l) => (
                            <div
                                key={l.title}
                                className="bg-coal-900 p-8 hover:bg-coal-700 transition-colors"
                            >
                                <l.icon className="w-8 h-8 text-ember mb-6" />
                                <h4 className="font-display text-xl font-semibold text-white mb-3">
                                    {l.title}
                                </h4>
                                <p className="text-zinc-400 leading-relaxed text-sm">
                                    {l.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROCESS TIMELINE */}
            <section className="section bg-coal-900">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-4">
                        From Enquiry to Stockyard
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white">
                        A predictable workflow.
                    </h2>
                    <div className="mt-12 border border-white/10">
                        {[
                            ["Day 0", "Enquiry & Indicative Quote"],
                            ["Day 1–2", "LOI / Contract Drafting"],
                            ["Day 3–5", "LC / Payment Terms Finalised"],
                            ["Day 7–30", "Loading at Origin Port"],
                            ["Day 25–45", "In-Transit Vessel Tracking"],
                            ["Day 45+", "Discharge & Inland Dispatch"],
                        ].map(([day, step], idx) => (
                            <div
                                key={day}
                                className={`flex flex-col md:flex-row md:items-center border-b border-white/10 last:border-b-0 ${
                                    idx % 2 === 0
                                        ? "bg-coal-900"
                                        : "bg-coal-800"
                                }`}
                            >
                                <div className="md:w-1/4 px-6 py-5 font-mono text-ember tracking-widest uppercase text-xs border-r border-white/5">
                                    {day}
                                </div>
                                <div className="md:flex-1 px-6 py-5 text-white font-display text-lg">
                                    {step}
                                </div>
                                <div className="hidden md:block px-6 py-5">
                                    <FlaskConical className="w-4 h-4 text-zinc-600" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section bg-coal-800 border-t border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
                    <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white leading-[1]">
                        Ready for a parcel
                        <br />
                        you don't have to babysit?
                    </h2>
                    <Link
                        to="/contact"
                        className="group bg-ember hover:bg-ember-hover text-white px-8 py-5 font-bold tracking-[0.18em] uppercase text-sm transition-colors flex items-center gap-3 self-start lg:self-auto"
                    >
                        Send your spec
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
